import React, { useCallback, useState } from 'react';
import { Divider, Dropdown } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import {
    IconAddCircle,
    IconBuildShape,
    IconCircle,
    IconImage,
    IconMusicLibrary,
    IconRectangle,
    IconTextField,
    IconVideo,
    IconVideoTemplate,
} from '@bynder/icons';
import { type MultiPageVideoModel } from '@bynder-studio/render-core';
import { type PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import PresetModal from '../presets/PresetsModal';
import AuthorizationHelper from '../../../helpers/AuthorizationHelper';
import permissions from '../../../configs/permissions';
import { AddAssetButton } from './EditorComponents.styled';
import useElementCreator from 'packages/pages/editor/useElementCreator';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import useEditor from '~/hooks/useEditor';
import usePresets from '../presets/usePresets';
import useCreateElementShortcuts from 'packages/hooks/shortcuts/useCreateElementShortcuts';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import features from '~/configs/features';
import useAccessRights from 'packages/hooks/useAccessRights';
import { WithShortcutLabel } from 'packages/common/WithShortcutLabel';

export const AddElementDropdown = () => {
    const { isPlatformAllowed } = useAccessRights();
    const { translate } = useTranslate();
    const { creativeModel, manipulationRenderer } = useEditor<MultiPageVideoModel, PlaybackManipulationRenderer>();
    const elementCreator = useElementCreator();
    const { applyPreset } = usePresets();
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [showContentSelectorModal, setShowContentSelectorModal] = useState(false);
    const [selectedObject, setSelectedObject] = useState({});
    const [assetType, setAssetType] = useState<'video' | 'image' | 'audio'>();

    const hasAudioFeature = isPlatformAllowed([features.NEW_AUDIO_ELEMENT]);

    const handleConfirmSelection = useCallback(
        (source) => {
            if (assetType === 'video') {
                elementCreator.createVideoElement(source);
            } else if (assetType === 'audio') {
                elementCreator.createAudioElement(source);
            } else {
                elementCreator.createImageElement(source);
            }

            setShowContentSelectorModal(false);
        },
        [creativeModel, manipulationRenderer, assetType],
    );

    const addElementFromSource = useCallback(
        (type: 'video' | 'image' | 'audio') => {
            const startFrame = manipulationRenderer.getCurrentFrame();
            const playbackDuration = (creativeModel as MultiPageVideoModel).getPlaybackDuration().getDuration();
            const duration = startFrame >= playbackDuration ? playbackDuration : playbackDuration - startFrame;

            setAssetType(type);
            setSelectedObject({ startFrame, duration });
            setShowContentSelectorModal(true);
        },
        [manipulationRenderer, creativeModel],
    );

    const addImageElement = useCallback(() => addElementFromSource('image'), [addElementFromSource]);
    const addVideoElement = useCallback(() => addElementFromSource('video'), [addElementFromSource]);
    const addAudioElement = useCallback(() => addElementFromSource('audio'), [addElementFromSource]);

    useCreateElementShortcuts(
        elementCreator,
        addImageElement,
        addVideoElement,
        hasAudioFeature ? addAudioElement : null,
    );
    usePresetShortcuts({ toggleModal: () => setShowPresetModal(!showPresetModal) });

    const handleAddPreset = useCallback(
        (selectedPreset, isUniqueContentProperties) => {
            const finalSelectedElement = selectedPreset;
            // Temporary solution
            finalSelectedElement.elements[Object.keys(finalSelectedElement.elements)[0]].name = selectedPreset.name;
            applyPreset(finalSelectedElement, isUniqueContentProperties);
            setShowPresetModal(false);
        },
        [applyPreset],
    );

    return (
        <>
            <Dropdown
                trigger={({ isOpen, ...triggerProps }) => (
                    <AddAssetButton isDisabled={false} variant="primary" icon={<IconAddCircle />} {...triggerProps}>
                        {translate('editor.timeline.new-element.title')}
                    </AddAssetButton>
                )}
            >
                {AuthorizationHelper.hasPermission(permissions.ASSETS.READ) && (
                    <>
                        <Dropdown.Item onClick={addVideoElement} icon={<IconVideo />}>
                            <WithShortcutLabel shortcut={['v']}>
                                {translate('editor.timeline.new-element.video')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={addImageElement} icon={<IconImage />}>
                            <WithShortcutLabel shortcut={['i']}>
                                {translate('editor.timeline.new-element.image')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                        {hasAudioFeature && (
                            <Dropdown.Item onClick={addAudioElement} icon={<IconMusicLibrary />}>
                                <WithShortcutLabel shortcut={['a']}>
                                    {translate('editor.timeline.new-element.audio')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                        )}
                    </>
                )}
                <Dropdown.Item onClick={elementCreator.createTextElement} icon={<IconTextField />}>
                    <WithShortcutLabel shortcut={['t']}>
                        {translate('editor.timeline.new-element.text')}
                    </WithShortcutLabel>
                </Dropdown.Item>
                <Dropdown.Item
                    icon={<IconBuildShape />}
                    submenu={
                        <>
                            <Dropdown.Item icon={<IconRectangle />} onClick={elementCreator.createRectangleElement}>
                                <WithShortcutLabel shortcut={['r']}>
                                    {translate('editor.timeline.new-element.rectangle')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                            <Dropdown.Item icon={<IconCircle />} onClick={elementCreator.createEllipseElement}>
                                <WithShortcutLabel shortcut={['e']}>
                                    {translate('editor.timeline.new-element.ellipse')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                        </>
                    }
                >
                    {translate('editor.timeline.new-element.shape')}
                </Dropdown.Item>
                <Divider />
                {AuthorizationHelper.hasPermissions([permissions.ASSETS.READ, permissions.PRESETS.READ]) && (
                    <Dropdown.Item icon={<IconVideoTemplate />} onClick={() => setShowPresetModal(true)}>
                        <WithShortcutLabel shortcut={['p']}>
                            {translate('editor.timeline.new-element.preset')}
                        </WithShortcutLabel>
                    </Dropdown.Item>
                )}
            </Dropdown>
            {AuthorizationHelper.hasPermission(permissions.PRESETS.READ) && showPresetModal && (
                <PresetModal toggleModal={(show) => setShowPresetModal(show)} confirmSelection={handleAddPreset} />
            )}
            {showContentSelectorModal && assetType && (
                <ContentPickerModal
                    type={assetType}
                    frameRate={(creativeModel as MultiPageVideoModel)?.getPlaybackDuration().getFrameRate()}
                    toggleModal={setShowContentSelectorModal}
                    show={showContentSelectorModal}
                    confirmSelection={handleConfirmSelection}
                    selectedObject={selectedObject}
                    resizableRange
                    isEditorPage
                />
            )}
        </>
    );
};
